import React, { useRef, useState, useEffect } from 'react';
import { useCachedBlobUrl } from '../../Services/useBlobDownloader';

const AudioMessagesPlayer = ({ audioKey, histogramData, playbackTime, onDelete, audioUrl }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsedTimeMs, setElapsedTimeMs] = useState(0);
  const [progress, setProgress] = useState(0);
  const actualPlaybackTime = playbackTime ? parseFloat(playbackTime) : 0;

  const [audioSrc, setAudioSrc] = useState(audioUrl || null);
  const { getCachedBlobUrl } = useCachedBlobUrl();

  // завантажуємо blob url
  useEffect(() => {
    if (audioSrc) return;

    if (!audioKey) {
      setAudioSrc(null);
      return;
    }

    const ac = new AbortController();

    (async () => {
      try {
        const blobUrl = await getCachedBlobUrl(audioKey, 'audio', { signal: ac.signal });
        setAudioSrc(blobUrl);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading audio:', err);
        }
      }
    })();

    return () => ac.abort();
  }, [audioKey, getCachedBlobUrl]);

  // нормалізація гістограми
  const formattedHistogramData = Array.isArray(histogramData)
    ? histogramData.map((v) => {
        const n = typeof v === 'string' ? parseFloat(v.replace('%', '')) : Number(v);
        const clamped = Math.round(Math.max(2, Math.min(100, n || 0)));
        return clamped;
      })
    : [];

  // play/pause
  const togglePlayAudio = () => {
    if (!audioRef.current || !audioSrc) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // оновлення прогресу і ресет по закінченню
  useEffect(() => {
    if (!audioRef.current || !audioSrc) return;

    const updateProgress = () => {
      if (!audioRef.current) return;
      setElapsedTimeMs(Math.floor(audioRef.current.currentTime * 1000));
      if (actualPlaybackTime > 0) {
        setProgress((audioRef.current.currentTime / actualPlaybackTime) * 100);
      }
    };

    const handleAudioEnd = () => {
      setIsPlaying(false);
      setElapsedTimeMs(0);
      setProgress(0);
    };

    if (isPlaying) {
      audioRef.current.addEventListener('timeupdate', updateProgress);
      audioRef.current.addEventListener('ended', handleAudioEnd);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', updateProgress);
        audioRef.current.removeEventListener('ended', handleAudioEnd);
      }
    };
  }, [isPlaying, actualPlaybackTime, audioSrc]);

  // клік по гістограмі = seek
  const handleSeek = (e) => {
    if (!audioRef.current || actualPlaybackTime <= 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const seekPercentage = clickX / width;
    const seekTime = seekPercentage * actualPlaybackTime;

    audioRef.current.currentTime = seekTime;
    setElapsedTimeMs(seekTime * 1000);
    setProgress(seekPercentage * 100);
  };

  // формат часу
  const formatPlaybackTime = (timeInMs) => {
    const totalSeconds = Math.floor(timeInMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleDeleteClick = () => {
    if (onDelete && audioKey) {
      onDelete(audioKey);
    }
  };

  return (
    <div className="audio-playback-container">
      <div className="audio-playback">
        {/* play / pause button */}
        <button
          onClick={togglePlayAudio}
          className={`play-button ${isPlaying ? 'stop' : 'play'}`}
          disabled={!audioSrc}
        >
          <div className="stop-bg-1" style={{ '--i': 1 }}></div>
          <div className="stop-bg-2" style={{ '--i': 2 }}></div>
          <div className="main-button"></div>
        </button>

        {/* сам <audio> */}
        <audio ref={audioRef} src={audioSrc}></audio>

        {/* waveform + таймер + (хрестик якщо можна видаляти) */}
        <div className="histogram-and-timer-container">
          {/* клікабельна гістограма */}
          <div
            className="histogram-container"
            style={{
              width: `${histogramData.length * 2 + 4 * (histogramData.length - 1)}px`,
            }}
            onClick={handleSeek}
          >
            <div className="histogram-progress" style={{ width: `${progress}%` }}></div>

            {formattedHistogramData.map((value, index) => (
              <React.Fragment key={index}>
                <div className="bar-col-wrap">
                  <div className="col-cap" style={{ height: `${100 - value}%` }}></div>
                  <div className="histogram-bar" style={{ height: `${value}%` }}></div>
                </div>
                <div className="bar-gap"></div>
              </React.Fragment>
            ))}
          </div>

          {/* таймер + (умовно) хрестик */}
          <div className="audio-timer-and-delete">
            <span className="playback-timer">
              {isPlaying || elapsedTimeMs > 0
                ? formatPlaybackTime(elapsedTimeMs)
                : formatPlaybackTime((actualPlaybackTime || 0) * 1000)}
            </span>

            {onDelete && (
              <button type="button" className="audio-remove-btn" onClick={handleDeleteClick}>
                ✕
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioMessagesPlayer;
